import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'redux';
import injectSheet from 'react-jss';
import cn from 'classnames';
import theme, { buttonReset, easeOutCubic, easeOutQuad } from '../../styles/theme';
import { wishlistToggleCreator } from '../../actions';
import { isProductWishlisted } from '../../selectors';
import GlobalMarkup from '../GlobalMarkup';

const hiddenSvgStyle = {
  position: 'absolute',
  width: 0,
  height: 0,
};

const GlobalSvg = () => (
  <GlobalMarkup id="wishlist-shadow-svg">
    <svg style={hiddenSvgStyle}>
      <defs>
        <filter id="wishlist-shadow" width="166%" height="166%" x="-33%" y="-33%">
          <feOffset in="SourceAlpha" dx="0" dy="1.5" />
          <feGaussianBlur stdDeviation="3.3" />
          <feComponentTransfer>
            <feFuncA type="linear" slope="0.4" />
          </feComponentTransfer>
          <feBlend />
        </filter>
      </defs>
      <symbol viewBox="0 0 50 50" id="symbol-wishlist">
        <path
          d="M31,9.9A16.84,16.84,0,0,1,29.09,18C26.73,22.64,22.33,26.85,16,30.5,9.67,26.85,5.27,22.64,2.91,18A16.8,16.8,0,0,1,1,9.94,8.28,8.28,0,0,1,9.11,1.5a8,8,0,0,1,5.25,2L16,4.9l1.64-1.43a8,8,0,0,1,5.25-2A8.27,8.27,0,0,1,31,9.9Z"
        />
      </symbol>
    </svg>
  </GlobalMarkup>
);

const WishlistToggle = ({
  checked, classes, className, onClick,
}) => (
  <button
    className={cn(className, classes.wishlist, checked && classes.checked)}
    onClick={onClick}
    type="button"
  >
    <GlobalSvg />
    <span className={classes.iconWrapper}>
      <svg className={cn(classes.icon, classes.shadow)} viewBox="0 0 50 50">
        <use xlinkHref="#symbol-wishlist" x="9" y="9" filter="url(#wishlist-shadow)" />
      </svg>
      <svg className={cn(classes.icon, classes.heart)} viewBox="0 0 50 50">
        <use xlinkHref="#symbol-wishlist" x="9" y="9" />
      </svg>
    </span>
  </button>
);

WishlistToggle.propTypes = {
  checked: PropTypes.bool.isRequired,
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
  className: PropTypes.string,
  onClick: PropTypes.func.isRequired,
};

WishlistToggle.defaultProps = {
  className: undefined,
};

const mapStateToProps = (state, { sku }) => ({
  checked: isProductWishlisted(state, sku),
});

const mapDispatchToProps = (dispatch, { sku }) => ({
  onClick: e => {
    e.preventDefault();
    e.stopPropagation();
    dispatch(wishlistToggleCreator(sku));
  },
});

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  injectSheet({
    wishlist: {
      fill: theme.colors.transparent,
      stroke: theme.colors.text,
      width: 38,
      position: 'absolute',
      top: 12,
      right: 12,
      ...buttonReset({
        display: 'block',
        transition: 'all 0.3s ease-out',
      }),
      '&:hover': {
        stroke: theme.colors.secondary,
        '& $shadow': {
          opacity: 1,
          transform: 'scale(1.001)',
        },
        '& $heart': {
          transform: 'scale(1.05)',
        },
      },
    },
    iconWrapper: {
      position: 'relative',
      display: 'block',
      '&::after': {
        content: '""',
        display: 'block',
        paddingTop: '100%',
      },
    },
    icon: {
      strokeWidth: 2,
      display: 'block',
      position: 'absolute',
      left: 0,
      top: 0,
      width: '100%',
      height: '100%',
    },
    heart: {
      transform: 'scale(1.001)',
      transition: `transform 0.4s ${easeOutQuad}`,
    },
    shadow: {
      fill: 'none',
      opacity: 0,
      transform: 'scale(0.85)',
      transition: `all 0.4s ${easeOutQuad}`,
    },
    checked: {
      stroke: theme.colors.secondary,
      '&, &:hover': {
        fill: theme.colors.secondary,
      },
      '& $heart': {
        animation: `heartbeat 0.84s ${easeOutCubic}`,
      },
    },
    '@keyframes heartbeat': {
      '0%': {
        transform: 'scale(1.05)',
      },
      '14%': {
        transform: 'scale(1.05)',
      },
      '29%': {
        transform: 'scale(1.2)',
      },
      '52%': {
        transform: 'scale(1.001)',
      },
      '62%': {
        transform: 'scale(1.001)',
      },
      '76%': {
        transform: 'scale(1.2)',
      },
      '100%': {
        transform: 'scale(1.05)',
      },
    },
  }),
)(WishlistToggle);
